import { MarketStatus, RunnerStatusMarketDetails } from '@ps/isp-web-services-types';
import type {
  CompetitionId,
  EventId,
  Handicap,
  LegTypes,
  MarketId,
  MarketType,
  Odds,
  RunnerResultType,
  SelectionId
} from '@ps/isp-web-services-types';
import { useTrackers } from '@ps/web-shared-analytics';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Selection as BetSlipSelection } from '@swf/core/models/sports/betSlip/types';
import { select } from '@swf/core/store';
import type { Dispatch, RootState } from '@swf/core/store';
import useSelectionWatcher from '@swf/sports/hooks/useSelectionWatcher';
import { EventTypeId } from '@swf/sports/types/eventType';

import type { ButtonProps } from './components/Button/Button';
import { Button } from './components/Button/Button';

export interface SelectionProps {
  eventId: EventId;
  eventTypeId: EventTypeId;
  competitionId?: CompetitionId;
  odds?: Odds;
  startTime: string;
}

interface OwnProps extends ButtonProps {
  handicap: Handicap | undefined;
  legTypes: LegTypes;
  marketId: MarketId;
  marketType?: MarketType;
  marketStatus: MarketStatus;
  runnerStatus?: RunnerStatusMarketDetails;
  runnerResultType: RunnerResultType | undefined;
  selectionId: SelectionId;
}

export type Props = OwnProps & SelectionProps;

const shouldAnimateUpdate = (
  marketStatus: MarketStatus,
  runnerStatus?: RunnerStatusMarketDetails
) => marketStatus === MarketStatus.OPEN && runnerStatus === RunnerStatusMarketDetails.ACTIVE;

export const Selection: React.FC<Props> = ({
  className,
  eventId,
  eventTypeId,
  competitionId,
  legTypes,
  marketId,
  marketStatus,
  marketType,
  odds,
  runnerStatus,
  selectionId,
  selectionText,
  startTime,
  size = 'base',
  handicap = 0,
  isMultiline = handicap !== 0,
  isStartingPriceMarket = false,
  runnerResultType
}) => {
  const { betSlip } = useDispatch<Dispatch>();
  const isSelectionInTheBetSlip = useSelector((rootState: RootState) =>
    select.betSlip.isSelectionInTheBetSlip(rootState, { marketId, selectionId })
  );
  const singleOpportunity = useSelector((rootState: RootState) =>
    select.betSlip.getSingleOpportunity(rootState, {
      selectionId,
      marketId
    })
  );

  const oddsHistory = useSelectionWatcher(odds, handicap);
  const { selectionAdded: trackSelectionAdded, selectionRemoved: trackSelectionRemoved } =
    useTrackers();

  const onClick = useCallback(() => {
    if (isSelectionInTheBetSlip) {
      betSlip.selectionRemoved({ marketId, selectionId });

      if (singleOpportunity) {
        trackSelectionRemoved(singleOpportunity);
      }
    } else {
      const selection: BetSlipSelection = {
        selectionId,
        marketId,
        eventId,
        legType: legTypes.find((legType) => legType === 'SIMPLE_SELECTION') ?? legTypes[0]
      };

      betSlip.selectionAdded(selection);

      trackSelectionAdded({
        ...selection,
        runnerStatus,
        startTime,
        odds,
        eventTypeId,
        competitionId,
        marketType
      });
    }
  }, [
    isSelectionInTheBetSlip,
    betSlip,
    marketId,
    selectionId,
    singleOpportunity,
    trackSelectionRemoved,
    eventId,
    legTypes,
    trackSelectionAdded,
    runnerStatus,
    startTime,
    odds,
    eventTypeId,
    competitionId,
    marketType
  ]);

  const isLocked =
    runnerStatus !== RunnerStatusMarketDetails.ACTIVE || marketStatus !== MarketStatus.OPEN;
  const isNonRunner = runnerStatus === RunnerStatusMarketDetails.REMOVED;
  const isDisabled = (isLocked || (!odds && !isStartingPriceMarket)) && !isSelectionInTheBetSlip;
  const isVacantTrap =
    eventTypeId === EventTypeId.GREYHOUND_RACING &&
    !odds &&
    runnerStatus === RunnerStatusMarketDetails.SUSPENDED;

  return (
    <Button
      className={className}
      isDisabled={isDisabled}
      isLocked={isLocked}
      isMultiline={isMultiline}
      isNonRunner={isNonRunner}
      isStartingPriceMarket={isStartingPriceMarket}
      isVacantTrap={isVacantTrap}
      oddsHistory={oddsHistory}
      onClick={onClick}
      selected={isSelectionInTheBetSlip}
      runnerResultType={runnerResultType}
      selectionText={selectionText}
      shouldAnimate={shouldAnimateUpdate(marketStatus, runnerStatus)}
      size={size}
    />
  );
};
